import React, { useEffect } from 'react';
import { Box, Page } from '@wix/design-system';
import { PageHeader, AppsSection } from '@wix/app-market-components';
import {
  appMarketSubCategoryClick,
  appMarketPageView,
} from '@wix/bi-logger-market/v2';

import s from '../pages.scss';
import { CenteredLoader } from '../../components/centered-loader';
import { useAppsSectionSharedPropsMapper } from '../../hooks/apps-section-props-mapper';
import { Path } from '../../models/junk-yard';
import { useJunkYard, useEssentials, useServices } from '../../contexts';
import { AppMarketPage } from '../../enums';
import { convertStringToBi } from '../../bi-services/utils';
import { getPageViewBiData } from '../../bi-services/events-data-parser';
import { FetcherDecorator } from '../../decorators/fetcher-decorator';
import type { CategoryPageData } from './api/category-page-data';
import { getCategoryPageData } from './api/category-page-data';

export interface CategoryPageProps {
  slug: string;
}

function CategoryPageComponent({
  slug,
  category,
  appGroupSections,
}: CategoryPageProps & CategoryPageData) {
  const { goto, routerData } = useJunkYard();
  const { biLogger } = useEssentials();
  const appsSectionSharedPropsMapper = useAppsSectionSharedPropsMapper();
  const page = AppMarketPage.CATEGORY;

  useEffect(() => {
    const urlParams = location.pathname.split('/')[3];
    biLogger.report(
      appMarketPageView(
        getPageViewBiData({
          routerData,
          urlParams,
          pageType: page,
          pageName: slug,
          subCategories: appGroupSections.map((section) => section.tagSlug),
        }),
      ),
    );
  }, []);

  const navigateToSubCategoryPage = (subCategorySlug: string) => {
    goto({
      path: Path.CATEGORY,
      slug: category.slug,
      subCategories: [subCategorySlug],
      referral: 'view_more_apps',
    });
  };

  return (
    <Page className={s.appMarketPage}>
      <Page.Content>
        <PageHeader
          primaryTitle={category.description}
          secondaryTitle={category.name}
          imageUrl={category.image}
        />
        {appGroupSections.map((appGroupSection, index) => {
          return (
            <Box direction="vertical" marginTop="36px">
              <AppsSection
                {...appsSectionSharedPropsMapper(
                  appGroupSection,
                  page,
                  slug,
                  index,
                )}
                onButtonClick={() => {
                  const subCategorySlug = appGroupSection.tagSlug;
                  biLogger.report(
                    appMarketSubCategoryClick({
                      type: 'view_more_apps',
                      subcategory_name: convertStringToBi(subCategorySlug),
                      category_name: convertStringToBi(category.slug),
                    }),
                  );
                  navigateToSubCategoryPage(subCategorySlug);
                }}
                titleSize="medium"
              />
            </Box>
          );
        })}
      </Page.Content>
    </Page>
  );
}

export function CategoryPage({ slug }: CategoryPageProps) {
  const { i18n } = useEssentials();
  const services = useServices();

  return (
    <FetcherDecorator
      initialFetch={() =>
        getCategoryPageData({
          services,
          slug,
          languageCode: i18n.language,
        })
      }
      loader={<CenteredLoader />}
      memoId={`category-page-${slug}-${i18n.language}`}
    >
      {({ data: categoryPageData }) => (
        <CategoryPageComponent {...categoryPageData} slug={slug} />
      )}
    </FetcherDecorator>
  );
}
